import {Icon, RadioCard, RadioCardProps, RadioGroup, Text} from "@blueprintjs/core";
import styled from "@emotion/styled";
import {Controller, useFormContext} from "react-hook-form";
import {COLORS} from "../../../../../constants/constants";
import {CardTypeV2} from "../constants/enums";
import {CompactCardIcon, DetailedCardIcon, MinimumCardIcon, MicroCardIcon} from "../../../../common/icons";
import {IManifestCardConfigurationsV2} from "../types/manifest-card-configurations.type";
const {BRIGHT_BLUE, COLOR_BORDER_SELECTED, TEXT_GRAY, TEXT_BLACK} = COLORS;

const CARD_PROPS: (RadioCardProps & {descriptions?: string[]; icon?: React.ReactElement})[] = [
  {
    label: "Detailed",
    value: CardTypeV2.Detailed,
    descriptions: ["4 stops", "12h/24h clock based on user settings", "224px of configurable space"],
    icon: <DetailedCardIcon />
  },
  {
    label: "Compact",
    value: CardTypeV2.Compact,
    descriptions: ["3 stops", "24h clock", "160px of configurable space"],
    icon: <CompactCardIcon />
  },
  {
    label: "Minimal",
    value: CardTypeV2.Minimal,
    descriptions: ["1 stop", "24h clock", "160px of configurable space"],
    icon: <MinimumCardIcon />
  },
  {
    label: "Micro",
    value: CardTypeV2.Micro,
    descriptions: [
      "No Stops shown, only total # of stops shown",
      "Driver Information shown",
      "Maximize the number of cards on the screen "
    ],
    icon: <MicroCardIcon />
  }
];

const CardTypeSelector = () => {
  const {control} = useFormContext<IManifestCardConfigurationsV2>();

  return (
    <Controller
      control={control}
      name="cardType"
      render={({field: {value, onChange}}) => {
        return (
          <StyledRadioGroup selectedValue={value} onChange={onChange}>
            {CARD_PROPS.map((cardProp) => {
              const isSelected = cardProp.value === value;
              return (
                <RadioCard
                  key={cardProp.label}
                  data-testid={`card-type-option-${cardProp.label}`}
                  value={cardProp.value}
                  alignIndicator="left"
                >
                  <CardTitle>
                    <Icon icon={cardProp.icon} />
                    {cardProp.label}
                  </CardTitle>
                  <CardDescription>
                    {cardProp.descriptions?.map((desc, index) => (
                      <Subtext key={index}>&nbsp;&nbsp;•&nbsp;{desc}</Subtext>
                    ))}
                  </CardDescription>
                  {isSelected && <SelectedIcon icon="tick-circle" color="#436BF8" />}
                </RadioCard>
              );
            })}
          </StyledRadioGroup>
        );
      }}
    />
  );
};

export default CardTypeSelector;

const StyledRadioGroup = styled(RadioGroup)`
  display: flex;
  flex-direction: row;
  gap: 12px;

  .bp5-card {
    position: relative;
    width: 285px;
    border-radius: 8px;

    &.bp5-interactive:hover {
      box-shadow: 0px 0px 8px 0px rgba(17, 20, 24, 0.1);
      -webkit-box-shadow: 0px 0px 8px 0px rgba(17, 20, 24, 0.2);
      -moz-box-shadow: 0px 0px 8px 0px rgba(17, 20, 24, 0.2);
      cursor: pointer;
    }

    &.bp5-interactive.bp5-selected {
      background-color: ${BRIGHT_BLUE};
      border: 1px solid ${COLOR_BORDER_SELECTED};
      box-shadow: none;
    }

    .bp5-radio {
      padding: 0 !important;
      padding-inline-start: 0px !important;

      .bp5-control-card-label {
        padding: 12px;
      }

      .bp5-control-indicator {
        display: none;
      }
    }
  }
`;

const CardTitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${TEXT_BLACK};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CardDescription = styled.div`
  font-size: 14px;
  margin-top: 8px;
  margin-left: 2px;
`;

const Subtext = styled(Text)`
  color: ${TEXT_GRAY};
  font-size: 12px;
  line-height: 16px;
`;

const SelectedIcon = styled(Icon)`
  position: absolute;
  top: 12px;
  right: 12px;
`;
